/**
 * MediaGallery — shared masonry + lightbox block used by both ShowcaseModal
 * and the LongForm in-app popup. Pulled out so the two share one visual
 * language and one lightbox integration with no duplication.
 *
 * Owns its lightbox state. Optionally accepts a per-photo "Try" CTA
 * (landing-only) via onTryWithPrompt. Hover-play for video tiles, caption
 * overlay, video badge — all included.
 */

import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ImageModal } from '../../ImageGalleryNew/ImageModal/ImageModal';
import type { GalleryMedia } from '../../ImageGalleryNew/types';
import '../../ImageGalleryNew/styles/tokens.css';
import OptimizedImage from '../../OptimizedImage/OptimizedImage';
import type { ShowcasePhoto } from '../ShowcaseModal/types';
import styles from './MediaGallery.module.css';

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlayIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <path d="M3 1.5v9l8-4.5z" />
  </svg>
);

export interface MediaGalleryProps {
  photos: ShowcasePhoto[];
  /** Used in lightbox parameters block. Ignored when hideLightboxParams=true. */
  modelName?: string;
  provider?: string;
  /** Hide the parameters block (model/prompt/etc) inside the lightbox.
   *  Default: true — appropriate for in-app broadcasts. */
  hideLightboxParams?: boolean;
  /** Per-photo "Try" CTA in the lightbox. When set, also accepts a label. */
  onTryWithPrompt?: (prompt: string) => void;
  /** Label for the per-photo CTA. Required when onTryWithPrompt is set. */
  tryLabel?: string;
  /** Optional callback fired after the per-photo CTA — ShowcaseModal uses
   *  it to close the outer modal too. */
  afterTry?: () => void;
  /** ARIA label fallback applied to a tile button when its photo has no
   *  caption / prompt. */
  ariaLabelFallback?: string;
}

export const MediaGallery = ({
  photos,
  modelName,
  provider,
  hideLightboxParams = true,
  onTryWithPrompt,
  tryLabel,
  afterTry,
  ariaLabelFallback,
}: MediaGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const galleryItems = useMemo<GalleryMedia[]>(
    () => photos.map((p, i) => ({
      id: `media-${i}`,
      type: p.type === 'video' ? 'video' : 'image',
      url: p.url,
      title: '',
      provider,
      metadata: {
        model: modelName || '',
        // Caption is fed into the lightbox's "Prompt" details block — that's
        // where the admin/user expects to see the per-media description.
        // Falls back to the photo's own prompt (landing FeaturedModel usage).
        prompt: p.caption || p.prompt || '',
        generationTime: '',
      },
    })),
    [photos, modelName, provider],
  );

  const selectedItem = selectedIndex !== null ? galleryItems[selectedIndex] : null;

  // Esc inside lightbox is owned by ImageModal — only react to it for the
  // gallery itself when no lightbox is open. The gallery as a whole has no
  // close behaviour, so this hook only nudges the parent if any.
  useEffect(() => {
    if (selectedIndex !== null) return;
  }, [selectedIndex]);

  const closeLightbox = () => setSelectedIndex(null);
  const goPrev = () =>
    selectedIndex !== null && selectedIndex > 0 && setSelectedIndex(selectedIndex - 1);
  const goNext = () =>
    selectedIndex !== null
    && selectedIndex < galleryItems.length - 1
    && setSelectedIndex(selectedIndex + 1);

  const handleSelectFromLightbox = (item: GalleryMedia) => {
    const idx = galleryItems.findIndex((x) => x.id === item.id);
    if (idx >= 0) setSelectedIndex(idx);
  };

  const tryAction = onTryWithPrompt && tryLabel
    ? {
        label: tryLabel,
        icon: <ArrowIcon />,
        onClick: () => {
          if (selectedItem) onTryWithPrompt(selectedItem.metadata.prompt);
          closeLightbox();
          afterTry?.();
        },
      }
    : undefined;

  if (photos.length === 0) return null;

  return (
    <>
      <div className={styles.gallery}>
        {photos.map((photo, i) => {
          const isVideo = photo.type === 'video';
          return (
            <button
              type="button"
              key={`${photo.url}-${i}`}
              className={styles.photo}
              onClick={() => setSelectedIndex(i)}
              aria-label={photo.caption || photo.prompt || ariaLabelFallback}
            >
              {isVideo ? (
                <video
                  src={photo.url}
                  muted
                  playsInline
                  preload="metadata"
                  loop
                  onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                  onMouseLeave={(e) => {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                  }}
                />
              ) : (
                <OptimizedImage
                  src={photo.url}
                  alt=""
                  loading="lazy"
                  draggable={false}
                  sizes="(max-width: 768px) 50vw, 33vw"
                />
              )}
              {isVideo && (
                <span className={styles.videoBadge}>
                  <PlayIcon />
                  <span>VIDEO</span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Lightbox — separate portal at body level so its fixed positioning,
          zoom toggle, sidebar collapse, mobile floating controls and thumbnail
          strip all work the same way as in the rest of the app. */}
      {selectedItem && createPortal(
        <ImageModal
          image={selectedItem}
          images={galleryItems}
          onClose={closeLightbox}
          onSelect={handleSelectFromLightbox}
          onPrev={goPrev}
          onNext={goNext}
          hasPrev={selectedIndex !== null && selectedIndex > 0}
          hasNext={selectedIndex !== null && selectedIndex < galleryItems.length - 1}
          hideDownload
          hideDelete
          hideParameters={hideLightboxParams}
          primaryAction={tryAction}
        />,
        document.body,
      )}
    </>
  );
};

export default MediaGallery;
